import {
  ScmAuth,
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  scmAuthApiRef,
} from '@backstage/integration-react';
import {
  ApiRef,
  BackstageIdentityApi,
  configApiRef,
  createApiRef,
  discoveryApiRef,
  OAuthApi,
  oauthRequestApiRef,
  ProfileInfoApi,
  SessionApi,
  errorApiRef,
  fetchApiRef,
  identityApiRef,
  storageApiRef,
  gitlabAuthApiRef,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import {
  ApiBlueprint,
  OverridableExtensionDefinition,
} from '@backstage/frontend-plugin-api';
import { UserSettingsStorage } from '@backstage/plugin-user-settings';
import { signalApiRef } from '@backstage/plugin-signals-react';
import LoginIcon from '@mui/icons-material/Login';

export namespace providersApiRefs {
  export const microsoftProviderAuthApiRef: ApiRef<
    OAuthApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
  > = createApiRef({
    id: 'microsoft-provider',
  });

  export const amsProviderAuthApiRef: ApiRef<
    OAuthApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
  > = createApiRef({
    id: 'ams-provider',
  });
}

export const apis: OverridableExtensionDefinition[] = [
  // SCM Integrations API
  ApiBlueprint.make({
    name: 'scmIntegrationsApiRef',
    params: defineParams =>
      defineParams({
        api: scmIntegrationsApiRef,
        deps: { configApi: configApiRef },
        factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
      }),
  }),

  // Auth APIs
  ApiBlueprint.make({
    name: 'scmAuthApiRef',
    params: defineParams =>
      defineParams({
        api: scmAuthApiRef,
        deps: {
          gitlabAuthApi: gitlabAuthApiRef,
        },
        factory: ({ gitlabAuthApi }) => ScmAuth.forGitlab(gitlabAuthApi),
      }),
  }),
  ApiBlueprint.make({
    name: 'gitlabAuthApiRef',
    params: defineParams =>
      defineParams({
        api: gitlabAuthApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          oauthRequestApi: oauthRequestApiRef,
          configApi: configApiRef,
        },
        factory: ({ discoveryApi, oauthRequestApi, configApi }) => {
          const authEnvironment =
            configApi.getOptionalString('auth.environment');

          return OAuth2.create({
            discoveryApi,
            oauthRequestApi,
            provider: {
              id: 'gitlab-provider',
              title: 'GitLab',
              icon: LoginIcon,
            },
            environment: authEnvironment,
            configApi,
          });
        },
      }),
  }),
  ApiBlueprint.make({
    name: 'microsoftProviderAuthApiRef',
    params: defineParams =>
      defineParams({
        api: providersApiRefs.microsoftProviderAuthApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          oauthRequestApi: oauthRequestApiRef,
          configApi: configApiRef,
        },
        factory: ({ discoveryApi, oauthRequestApi, configApi }) => {
          const authEnvironment =
            configApi.getOptionalString('auth.environment');
          const clientId = configApi.getString(
            `auth.providers.microsoft-provider.${authEnvironment}.clientId`,
          );

          return OAuth2.create({
            discoveryApi,
            oauthRequestApi,
            provider: {
              id: providersApiRefs.microsoftProviderAuthApiRef.id,
              title: 'Microsoft',
              icon: LoginIcon,
            },
            environment: authEnvironment,
            defaultScopes: [
              'openid',
              'profile',
              'email',
              `api://${clientId}/access_as_user`,
            ],
            configApi,
          });
        },
      }),
  }),
  ApiBlueprint.make({
    name: 'amsProviderAuthApiRef',
    params: defineParams =>
      defineParams({
        api: providersApiRefs.amsProviderAuthApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          oauthRequestApi: oauthRequestApiRef,
          configApi: configApiRef,
        },
        factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
          OAuth2.create({
            discoveryApi,
            oauthRequestApi,
            provider: {
              id: providersApiRefs.amsProviderAuthApiRef.id,
              title: 'AMS',
              icon: LoginIcon,
            },
            environment: configApi.getOptionalString('auth.environment'),
            defaultScopes: ['openid', 'profile', 'email'],
            configApi,
          }),
      }),
  }),

  // User Settings Storage API
  ApiBlueprint.make({
    name: 'userSettingsStorageApiRef',
    params: defineParams =>
      defineParams({
        api: storageApiRef,
        deps: {
          discoveryApi: discoveryApiRef,
          errorApi: errorApiRef,
          fetchApi: fetchApiRef,
          identityApi: identityApiRef,
          signalApi: signalApiRef,
        },
        factory: deps => UserSettingsStorage.create(deps),
      }),
  }),
];
